// src/modules/IntegratedMessaging/components/containers/Main.tsx
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import { configurationPageContentClass } from '@modules/configuration/components/configurationLayout';
import { CardList } from '../display/CardList';
import { useIntegratedMessaging } from '../../hooks/useIntegratedMessaging';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { ErrorState } from '../feedback';
import { CreateButton } from '../interaction/CreateButton';
import { IntegratedMessagingSkeleton } from '../skeletons';
import { EditIntegrationModal, EditIntegrationModalRef } from '../modals/EditIntegrationModal';
import { CreateIntegrationModal, CreateIntegrationModalRef } from '../modals/CreateIntegrationModal';
import type { Courier } from '../../models/IntegratedMessaging';

export const Main = () => {
  const { t } = useTranslation('integratedmessagingModule');
  const { data, isLoading, error, refetch } = useIntegratedMessaging();

  const editModalRef = useRef<EditIntegrationModalRef>(null);
  const createModalRef = useRef<CreateIntegrationModalRef>(null);

  const handleCreate = (courier: Courier) => {
    createModalRef.current?.open(courier);
  };

  if (isLoading) return <IntegratedMessagingSkeleton />;
  if (error) {
    return (
      <div className="w-full pb-6">
        <ErrorState error={error} onRetry={refetch} className="h-auto py-16" />
      </div>
    );
  }

  const hasIntegrations = Boolean(data && data.length > 0);

  return (
    <div className="w-full pb-6" data-testid="integrated-messaging-view">
      <ConfigurationPageHeader
        title={t('title')}
        description={t('subtitle')}
        action={hasIntegrations ? <CreateButton onCreate={handleCreate} /> : undefined}
      />

      <div className={configurationPageContentClass}>
        {!hasIntegrations ? (
          <EmptyView
            type="emptyState"
            icon={EMPTY_VIEW_ICONS.DEFAULT}
            customAction={
              <div className="flex justify-center">
                <CreateButton onCreate={handleCreate} />
              </div>
            }
          />
        ) : (
          <CardList
            data={data!}
            refetch={refetch}
            modalRef={editModalRef as React.RefObject<EditIntegrationModalRef>}
          />
        )}
      </div>

      <EditIntegrationModal ref={editModalRef} />
      <CreateIntegrationModal ref={createModalRef} />
    </div>
  );
};
